<template>
  <div>
    <h1>
      Synchronization
    </h1>
    <div class="row">
      <div v-if="!authenticated" class="col-lg-6 col-sm-12">
        <form>
          <div class="alert alert-warning" role="alert" v-if="countClosedSurveys > 0">
            <i class="fa fa-fw fa-info-circle"></i>
            You have closed surveys. Please login to export closed surveys to the server.
          </div>
          <div v-if="signInError" class="form-group-row">
            <div class="alert alert-danger" role="alert">
              <strong>
                <i class="fa fa-fw fa-exclamation-triangle"></i>
                Login attempt failed
              </strong>
            </div>
          </div>
          <div class="form-group row">
            <label for="username" class="col-sm-2 col-form-label">Username</label>
            <div class="col-sm-10">
              <input @keyup.enter="login" id="username" v-model="username" type="text" class="form-control">
            </div>
          </div>
          <div class="form-group row">
            <label for="password" class="col-sm-2 col-form-label">Password</label>
            <div class="col-sm-10">
              <input @keyup.enter="login" id="password" v-model="password" type="password" class="form-control">
            </div>
          </div>
          <div class="qm-survey-button-row">
            <button @click="login" type="button" class="btn btn-turquois qm-survey-edit-button">
              <i v-show="!isSigningIn" class="fa fa-fw fa-lock"></i>
              <i v-show="isSigningIn" class="fa fa-fw fa-spinner fa-spin"></i>
              Login
            </button>
          </div>
        </form>
      </div>
      <div v-else class="col-lg-6 col-sm-12">
        <div class="qm-panel qm-panel-left">
          <div id="qm-survey-main-buttons">

            <h2>Fetch</h2>
            <div v-if="session.lastFetch">Last fetch: {{ session.lastFetch | formatDateTime }}</div>

            <div v-if="countClosedSurveys === 0">
              <button :disabled="isFetchingFromServer || isExportingToServer" v-on:click="fetchSurveysAndResources" type="button" class="btn  btn-turquois ripple">
                <i v-if="!isFetchingFromServer" class="fa fa-fw fa-cloud-download"></i>
                <i v-else class="fa fa-fw fa-spinner fa-spin"></i>
                Fetch Surveys from Server
              </button>
            </div>
            <div v-else>
              <div class="alert alert-warning" role="alert">
                <strong>
                  <i class="fa fa-fw fa-info-circle"></i>
                  You have closed surveys.
                </strong><br>
                Please export closed surveys to the server before fetching new surveys from the server!
              </div>
            </div>

            <div v-if="fetchNoNetwork" class="no-network">
              <div class="alert alert-danger" role="alert">
                <strong><i class="fa fa-fw fa-exclamation-triangle"></i> No connection to QM server</strong><br />Please check your internet connection.
              </div>
            </div>

            <h2>Export</h2>
            <div v-if="countClosedSurveys === 0">
              <p>
                No closed surveys found
              </p>
            </div>
            <button v-else :disabled="isExportingToServer || isFetchingFromServer" v-on:click="exportEverything" type="button" class="btn btn-turquois btn-turquois qm-survey-edit-button">
              <i v-if="!isExportingToServer" class="fa fa-fw fa-cloud-upload"></i>
              <i v-else class="fa fa-fw fa-spinner fa-spin"></i>
              Export {{ countClosedSurveys }} Closed Survey<span v-if="countClosedSurveys > 1">s</span>
            </button>
            <div v-if="exportError" class="no-network">
              <div class="alert alert-danger" role="alert">
                <strong><i class="fa fa-fw fa-exclamation-triangle"></i> {{ exportErrorMessage }}</strong>
              </div>
            </div>
          </div>
        </div>

        <div v-if="countDeletedSurveys > 0" class="qm-panel qm-panel-left">
          <h2>Transmitted Surveys</h2>
          <div>
            <p>
              Your app contains {{ countDeletedSurveys === 1 ? 'a survey that has' : countDeletedSurveys + ' surveys hat have' }}
              been transmitted to the server. In case the transfer did not work, you have the possibility to re-transfer these
              surveys.
            </p>
            <p>
              <button :disabled="isFetchingFromServer || isExportingToServer" @click="repeatExport" class="btn btn-warning">
                Reopen Surveys for Transfer
              </button>
            </p>
            <p>
              Otherwise, please delete transferred surveys periodically to free resources for new surveys. <strong>Before deleting,
              please make sure that the records were properly transferred to the QualiMover server.</strong>
            </p>
            <p>
              <button :disabled="isFetchingFromServer || isExportingToServer" @click="deleteConfirm" class="btn btn-warning">
                Delete
              </button>
            </p>

            <div v-show="deleteConfirmed" class="deleteConfirm">
              <div class="alert alert-warning">
                <p>
                  You are about to delete transmitted surveys from the local app. Please type in "delete" to confirm:
                </p>
                <p>
                  <input type="text" v-model="deleteConfirmPrompt" class="form-control"/>
                </p>
                <div v-if="deleteConfirmError" class="alert alert-danger">
                  {{ deleteConfirmError }}
                </div>
                <button :disabled="isFetchingFromServer || isExportingToServer" @click="deleteTransmittedSurveys" type="button" class="btn btn-danger">
                  <i class="fa fa-fw fa-trash"></i>
                  Delete Now
                </button>
                <button :disabled="isFetchingFromServer || isExportingToServer" @click="cancelDelete" type="button" class="btn btn-turquois">
                  <i class="fa fa-fw fa-times"></i>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
        <p>
          <button :disabled="isFetchingFromServer || isExportingToServer" @click="logout" type="button" class="btn btn-turquois qm-survey-edit-button mb-5">
            <i class="fa fa-fw fa-sign-out"></i> Logout
          </button>
        </p>
      </div>

      <div v-if="fetched && !fetchNoNetwork" class="col-lg-6 col-sm-12">
        <div v-if="!hasSurveys">
          <div class="alert alert-warning" role="alert">
            <i class="fa fa-fw fa-info-circle"></i>
            There are no surveys available on the server. Please register a new survey on the server first.
          </div>
        </div>

        <div class="qm-panel qm-panel-left">
          <table class="table">
            <tbody>
            <tr class="td-highlight">
              <td colspan="2">Fetch Results</td>
            </tr>
            <tr>
              <td>Surveys</td>
              <td width=100 v-if="countFetchedSurveys > 0">{{ countFetchedSurveys }}</td>
              <td width=100 v-else-if="isFetchingFromServer"><i class="fa fa-spinner fa-spin"></i></td>
              <td width=100 v-else>0</td>
            </tr>
            <tr>
              <td>Room names</td>
              <td v-if="countFetchedRoomNames > 0">{{ countFetchedRoomNames }}</td>
              <td v-else-if="isFetchingFromServer"><i class="fa fa-spinner fa-spin"></i></td>
              <td v-else>0</td>
            </tr>
            <tr>
              <td>Standard survey items</td>
              <td v-if="countFetchedStandardSurveyItems > 0">{{ countFetchedStandardSurveyItems }}</td>
              <td v-else-if="isFetchingFromServer"><i class="fa fa-spinner fa-spin"></i></td>
              <td v-else>0</td>
            </tr>
            <tr>
              <td>Modes</td>
              <td v-if="countFetchedModes > 0">{{ countFetchedModes }}</td>
              <td v-else-if="isFetchingFromServer"><i class="fa fa-spinner fa-spin"></i></td>
              <td v-else>0</td>
            </tr>
            <tr>
              <td>Countries</td>
              <td v-if="countFetchedCountries > 0">{{ countFetchedCountries }}</td>
              <td v-else-if="isFetchingFromServer"><i class="fa fa-spinner fa-spin"></i></td>
              <td v-else>0</td>
            </tr>
            <tr>
              <td>Crates</td>
              <td v-if="countFetchedCrates > 0">{{ countFetchedCrates }}</td>
              <td v-else-if="isFetchingFromServer"><i class="fa fa-spinner fa-spin"></i></td>
              <td v-else>0</td>
            </tr>
            <tr>
              <td>Vehicle types</td>
              <td v-if="countFetchedVehicleTypes > 0">{{ countFetchedVehicleTypes }}</td>
              <td v-else-if="isFetchingFromServer"><i class="fa fa-spinner fa-spin"></i></td>
              <td v-else>0</td>
            </tr>
            <tr>
              <td>Legal notes</td>
              <td v-if="countFetchedLegalNotes > 0">{{ countFetchedLegalNotes }}</td>
              <td v-else-if="isFetchingFromServer"><i class="fa fa-spinner fa-spin"></i></td>
              <td v-else>0</td>
            </tr>
            </tbody>
          </table>
          <p>
            <button v-on:click="closeFetchResults" class="btn btn-turquois">
              <i class="fa fa-fw fa-times"></i> Close
            </button>
          </p>
        </div>
      </div>

      <div v-if="exportStarted" class="export-success col-lg-6 col-sm-12">
        <div class="qm-panel qm-panel-left">
          <table class="table">
            <tbody>
            <tr class="td-highlight">
              <td colspan="2" v-if="isExportingToServer"><i class="fa fa-fw fa-check-circle"></i> Export started</td>
              <td colspan="2" v-else><i class="fa fa-fw fa-check-circle"></i> Export completed</td>
            </tr>
            <tr>
              <td>Exported Surveys</td>
              <td>
                <span v-if="!isExportingToServer">{{ countExportedSurveys }}</span>
                <span v-else>
                  <i class="fa fa-fw fa-spinner fa-spin fa-fw"></i>
                  <span class="sr-only">Sending...</span>
                </span>
              </td>
            </tr>
            <tr>
              <td>Exported Signatures</td>
              <td>
                <span v-if="!isExportingToServer">{{ countExportedSignatures }}</span>
                <span v-else>
                  <i class="fa fa-fw fa-spinner fa-spin fa-fw"></i>
                  <span class="sr-only">Sending...</span>
                </span>
              </td>
            </tr>
            <tr>
              <td>Exported Photos</td>
              <td>
                <span v-if="!isExportingToServer">{{ countExportedFotos }}</span>
                <span v-else>
                  <i class="fa fa-fw fa-spinner fa-spin fa-fw"></i>
                  <span class="sr-only">Sending...</span>
                </span>
              </td>
            </tr>
            <tr v-if="!isExportingToServer && exportSuccess" class="td-highlight">
              <td colspan="2">
                <i class="fa fa-fw fa-check-circle"></i>
                Export completed
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
  button {
    margin: 0.5em 0;
  }
  p {
    margin: 0.5em 0;
  }
</style>

<script>
  import { mapGetters } from 'vuex'
  import axios from 'axios'
  import $ from 'jquery'
  import moment from 'moment'
  import Login from './children/Login.vue'

  export default {
    name: 'Sync',
    components: {
      'login': Login
    },
    data: () => ({
      username: '',
      password: '',
      fetched: false,
      deleteConfirmed: false,
      deleteConfirmPrompt: '',
      deleteConfirmError: null,
      countClosedSurveys: 0,
      countExportedSurveys: 0,
      countExportedSignatures: 0,
      countExportedFotos: 0,
      countFetchedSurveys: 0,
      countFetchedRoomNames: 0,
      countFetchedStandardSurveyItems: 0,
      countFetchedModes: 0,
      countFetchedCountries: 0,
      countFetchedCrates: 0,
      countFetchedVehicleTypes: 0,
      countFetchedLegalNotes: 0,
      countDeletedSurveys: 0,
      itemIdsQM: [],
      exportStarted: false,
      exportError: false,
      exportSuccess: false,
      exportFotosFinished: false,
      fetchNoNetwork: false,
      exportNoNetwork: false,
      exportErrorMessage: '',
      isSigningIn: false,
      signInError: false,
      hasSurveys: true,
      currentFetching: [],
      currentExporting: []
    }),
    computed: {
      ...mapGetters({
        session: 'getSession',
        surveys: 'getAllSurveys',
        rooms: 'getAllRooms',
        items: 'getAllItems',
        fotos: 'getAllFotos'
      }),
      authenticated () {
        return (this.session && this.session.token && this.session.username)
      },
      isFetchingFromServer () {
        return (this.currentFetching.length > 0)
      },
      isExportingToServer () {
        return (this.currentExporting.length > 0)
      }
    },
    created: function () {
      this.$initLog({
        url: this.$getApiHost() + '/debugger/log',
        consoleOutput: true
      })
      this.refreshSurveys()
    },
    methods: {
      refreshSurveys () {
        this.$store.dispatch('loadSession').then(() => {
          this.username = this.session.username
          this.$store.dispatch('loadAllSurveys').then(() => {
            this.countClosedSurveys = this.$store.getters.getClosedSurveys.length
            this.countDeletedSurveys = this.$store.getters.getDeletedSurveys.length
          })
          this.$store.dispatch('loadAllRooms')
          this.$store.dispatch('loadAllItems')
          this.$store.dispatch('loadAllFotos')
          this.$store.dispatch('loadAllResources')
        })
      },
      login () {
        this.isSigningIn = true
        this.signInError = false
        let data = new FormData() // eslint-disable-line no-undef
        data.append('username', this.username.trim())
        data.append('password', this.password.trim())
//        data.append('XDEBUG_SESSION_START', '')
        return axios.request({
          url: this.$getApiHost() + '/session',
          method: 'post',
          data: data
        }).then((result) => {
          if (result.data.authenticated === true) {
            this.$store.dispatch('setUsername', this.username).then(() => {
              this.$store.dispatch('setToken', result.data.token).then(() => {
              })
            })
          } else {
            this.signInError = true
          }
          this.isSigningIn = false
        }).catch((error) => {
          this.signInError = true
          this.isSigningIn = false
          this.$logException({
            error: error,
            message: 'network error on login',
            info: { surveyor: this.session.username }
          })
        })
      },
      logout () {
        this.closeFetchResults()
        this.closeExportResults()
        this.$store.dispatch('unsetToken')
        this.$store.dispatch('setUsername', '')
        this.$store.dispatch('setSudoUsername', '')
        let data = new FormData() // eslint-disable-line no-undef
        data.append('username', this.session.username)
        data.append('token', this.session.token)
        return axios.request({
          url: this.$getApiHost() + '/session/logout',
          method: 'post',
          data: {
            username: this.session.username,
            token: this.session.token
          }
        }).catch((error) => {
          this.$logException({
            error: error,
            message: 'network error on logout',
            info: { surveyor: this.session.username }
          })
        })
      },
      repeatExport: function () {
        // set transmitted surveys back to closed state
        let deletedSurveys = this.$store.getters.getDeletedSurveys
        let toUndelete = deletedSurveys.length
        let undeleteCount = 0
        $.each(deletedSurveys, (surveyKey, survey) => {
          this.$store.dispatch('setSurveyAsUndeleted', survey.surveyId).then(() => {
            undeleteCount++
            if (undeleteCount === toUndelete) {
              this.refreshSurveys()
            }
          })
        })
      },
      exportEverything: function () {
        this.exportError = false
        this.exportErrorMessage = ''
        this.exportStarted = true
        this.countExportedSurveys = 0
        this.countExportedSignatures = 0
        this.countExportedFotos = 0
        this.exportQMSurveys()
          .then((count) => {
            this.countExportedSurveys = count
            this.exportQMSignatures()
              .then(() => {
                this.exportQMFotos()
                  .then(() => {
                    this.exportFotosFinished = true
                    this.exportSuccess = true
                    this.refreshSurveys()
                  })
                  .catch((e) => {
                    this.exportError = true
                    this.exportErrorMessage = e
                  })
              })
              .catch((e) => {
                this.exportError = true
                this.exportErrorMessage = e
              })
          })
          .catch((e) => {
            this.exportError = true
            this.exportErrorMessage = e
          })
      },
      exportQMSurveys () {
        const exportname = 'exportQMSurveys'
        this.registerExporting(exportname)
        return new Promise((resolve, reject) => {
          let closedSurveys = this.$store.getters.getClosedSurveys
          let allRooms = this.$store.getters.getAllRooms
          let allItems = this.$store.getters.getAllItems
          let allResources = this.$store.getters.getAllResources

          if (closedSurveys.length === 0) {
            reject('no closed surveys')
          } else {
            let surveysToExport = $.extend(true, {}, closedSurveys)
            $.each(surveysToExport, (surveyKey, surveyValue) => {
              // delete the signature. it's sent in another request
              delete surveysToExport[surveyKey].signatureDataUrl
              // get resources for survey
              let resources = allResources.filter((resource) => {
                return Number(resource.surveyId) === Number(surveyValue.surveyId)
              })
              surveysToExport[surveyKey].resources = resources
              // get rooms for survey
              let rooms = allRooms.filter((room) => {
                return Number(room.surveyId) === Number(surveyValue.surveyId)
              })
              surveysToExport[surveyKey].rooms = rooms
              // get items for rooms
              $.each(rooms, (roomKey, roomValue) => {
                let items = allItems.filter((item) => {
                  return item.roomId === roomValue.roomId
                })
                surveysToExport[surveyKey].rooms[roomKey].items = items
              })
            })

            axios.request({
              url: this.$getApiHost() + '/survey',
              method: 'post',
              data: {
                token: this.session.token,
                survey: surveysToExport
              }
            })
              .then((result) => {
                this.resolveExporting(exportname)
                let deletedSurveys = Object.keys(result.data)
                /* TODO: Looks like this is the result from QM_API_Survey::set(). First array element is always 'status'.
                    That's probably the reason why the 'Exported Surveys' counter in QualiSurveyor is always 1 too big.
                    Looks like setSurveyAsDeleted rejects this as 'no survey id', but the forEach
                    silently discards the rejection, because it doesn't even look at the result of `this.$store.dispatch`. */
                if (deletedSurveys.length > 0) {
                  this.itemIdsQM = this.reformatSurveyResponse(result.data)
                  deletedSurveys.forEach((surveyId) => {
                    this.$store.dispatch('setSurveyAsDeleted', surveyId)
                  })
                  resolve(deletedSurveys.length)
                } else if (result.status === 200 && closedSurveys.length) {
                  closedSurveys.forEach((survey) => {
                    this.$store.dispatch('setSurveyAsDeleted', survey.surveyId)
                  })
                  resolve(closedSurveys.length)
                } else {
                  reject('export of survey failed')
                }
              })
              .catch((e) => {
                // network error
                this.$logException({
                  error: e,
                  message: 'network error',
                  info: { surveyor: this.session.username }
                })
                this.resolveExporting(exportname)
                reject('no Internet connection or problem in QM API')
              })
          }
        })
      },
      exportQMSignatures () {
        let closedSurveys = this.$store.getters.getClosedSurveys
        return Promise.all(closedSurveys.map((survey) => {
          return this.exportQMSignature(survey)
        }))
      },
      exportQMSignature (survey) {
        const exportname = 'exportQMSignature'
        this.registerExporting(exportname)
        return new Promise((resolve) => {
          let signatureBlob = this.dataURItoBlob(survey.signatureDataUrl)
          let data = new FormData() // eslint-disable-line no-undef
          data.append('file', signatureBlob, 'signature.png')
          data.append('token', this.session.token)

          axios.request({
            url: this.$getApiHost() + '/photo/signature/' + survey.surveyId,
            method: 'post',
            data: data,
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
            .then(() => {
              this.countExportedSignatures++
              this.resolveExporting(exportname)
              resolve()
            })
            .catch((e) => {
              console.log('error', e)
              this.resolveExporting(exportname)
            })
        })
      },
      isHttpStatusOk (status) {
        if ((status >= 200) && (status < 299)) {
          return true
        } else if (status === 401) {
          this.$logException('unauthorized access')
        } else {
          this.$logException('http status not ok: ' + status)
        }
        return false
      },
      reformatSurveyResponse (response) {
        let matchTable = []
        Object.keys(response).forEach((currentValue, index) => {
          Object.keys(response[currentValue]).forEach((currentValue2, index2) => {
            matchTable.push({ oldId: currentValue2, newId: response[currentValue][currentValue2] })
          })
        })
        return matchTable
      },
      getQMItemId (localItemId) {
        let result = this.itemIdsQM.filter((obj) => {
          return Number(obj.oldId) === Number(localItemId)
        })
        if (result && result[0]) {
          return result[0].newId
        }
        return false
      },
      exportQMFotos () {
        return new Promise((resolve, reject) => {
          let closedSurveys = this.$store.getters.getClosedSurveys
          let closedSurveyIds = closedSurveys.map((survey) => { return survey.surveyId })
          let fotosFromClosedSurveys = this.$store.getters.getFotosOfClosedSurveys(closedSurveyIds)
          if (fotosFromClosedSurveys.length > 0) {
            return Promise.all(fotosFromClosedSurveys.map(foto => {
              return this.exportQMFoto(foto)
            }))
              .then(() => {
                resolve()
              })
              .catch(error => {
                console.log('error', error)
                reject('failed to transmit the photos')
              })
          } else {
            resolve()
          }
        })
      },
      exportQMFoto (foto) {
        const exportname = 'exportQMFoto'
        this.registerExporting(exportname)
        return new Promise((resolve, reject) => {
          let fotoBlob = this.dataURItoBlob(foto.dataUrl)

          let url = ''
          let filename = ''
          if (foto && foto.itemId && this.getQMItemId(foto.itemId)) {
            url = '/photo/item/' + this.getQMItemId(foto.itemId)
            filename = 'si-' + foto.itemId + '-' + foto.id + '.jpg'
          } else {
            url = '/photo/survey/' + foto.surveyId
            filename = 's-' + foto.surveyId + '-' + foto.id + '.jpg'
          }
          let data = new FormData() // eslint-disable-line no-undef
          data.append('id', foto.id)
          data.append('file', fotoBlob, filename)
          data.append('token', this.session.token)

          axios.request({
            url: this.$getApiHost() + url,
            method: 'post',
            data: data,
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
            .then(() => {
              this.countExportedFotos++
              this.resolveExporting(exportname)
              resolve()
            })
            .catch(error => {
              console.log('error', error)
              this.resolveExporting(exportname)
              reject(error)
            })
        })
      },
      dataURItoBlob (dataURI) {
        // http://stackoverflow.com/questions/4998908/convert-data-uri-to-file-then-append-to-formdata
        // http://stackoverflow.com/a/5100158

        // convert base64 to raw binary data held in a string
        // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
        let byteString = atob(dataURI.split(',')[1]) // eslint-disable-line no-undef

        // separate out the mime component
        let mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

        // write the bytes of the string to an ArrayBuffer
        let ab = new ArrayBuffer(byteString.length)
        let ia = new Uint8Array(ab)
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i)
        }

        // write the ArrayBuffer to a blob, and you're done
        return new Blob([ab], {type: mimeString}) // eslint-disable-line no-undef
      },
      fetchSurveysAndResources () {
        if (this.authenticated) {
          this.countFetchedSurveys = 0
          this.countFetchedRoomNames = 0
          this.countFetchedStandardSurveyItems = 0
          this.countFetchedModes = 0
          this.countFetchedCountries = 0
          this.countFetchedCrates = 0
          this.countFetchedVehicleTypes = 0
          this.countFetchedLegalNotes = 0
          this.fetchQMSurveys()
            .then(() => {
              this.fetchQMRoomNames()
              this.fetchQMStandardSurveyItems()
              this.fetchQMModes()
              this.fetchQMCountries()
              this.fetchQMCrates()
              this.fetchQMVehicleTypes()
              this.fetchQMLegalNotes()
              this.refreshSurveys()
            })
            .catch(() => {
              this.fetchNoNetwork = true
            })
        } else {
          console.log('%c No username defined! ', 'background: #222; color: #bada55')
          this.logout()
        }
      },
      fetchQMSurveys: function () {
        const fetchname = 'fetchQMSurveys'
        this.registerFetching(fetchname)
        this.hasSurveys = true
        return new Promise((resolve, reject) => {
          this.fetched = true
          axios.request({
            url: this.$getApiHost() + '/survey',
            method: 'get',
            params: {
              token: this.session.token,
              surveyor: this.session.username
            }
          })
            .then((response) => {
              let fetchedSurveyIds = []
              this.countFetchedSurveys = 0
              if (this.isHttpStatusOk(response.status) && (response.data.surveys !== null) && (response.data.surveys.length > 0)) {
                response.data.surveys.forEach((survey) => {
                  if (survey) {
                    this.countFetchedSurveys++
                    fetchedSurveyIds.push(survey.surveyId)
                    this.$store.dispatch('addSurvey', survey)
                  } else {
                    console.log('%c survey = null ', 'background: orange; color: black')
                  }
                })
                // set surveys as deleted that no longer exist on the server
                this.$store.dispatch('loadAllSurveys').then((localSurveys) => {
                  for (let localSurvey of localSurveys) {
                    if (!fetchedSurveyIds.includes(localSurvey.surveyId)) {
                      this.$store.dispatch('setSurveyAsDeleted', localSurvey.surveyId)
                    }
                  }
                })
                resolve()
              } else {
                this.hasSurveys = false
                console.log('%c fetchQMSurveys survey empty ', 'background: red; color: white')
              }
              this.resolveFetching(fetchname)
            })
            .catch(() => {
              reject()
            })
        })
      },
      fetchQMRoomNames: function () {
        const fetchname = 'fetchQMRoomNames'
        this.registerFetching(fetchname)
        axios.request({
          url: this.$getApiHost() + '/resource/room',
          method: 'get'
        }).then((response) => {
          if (response.data.rooms !== null) {
            if (response.data.rooms.length > 0) {
              this.$store.dispatch('clearRoomNameStorage').then(() => {
                this.$store.dispatch('importRoomNames', response.data.rooms).then(() => {
                  this.countFetchedRoomNames = response.data.rooms.length
                })
              })
            } else {
              console.log('%c fetchQMRoomNames rooms empty ', 'background: red; color: white')
            }
          } else {
            console.log('%c fetchQMRoomNames rooms null ', 'background: red; color: white')
          }
          this.resolveFetching(fetchname)
        })
      },
      fetchQMStandardSurveyItems: function () {
        const fetchname = 'fetchQMStandardSurveyItems'
        this.registerFetching(fetchname)
        axios.request({
          url: this.$getApiHost() + '/resource/standardItem',
          method: 'get'
        }).then((response) => {
          if (response.data.standardSurveyItems !== null) {
            if (response.data.standardSurveyItems.length > 0) {
              this.$store.dispatch('clearStandardSurveyItemsStorage').then(() => {
                this.$store.dispatch('importStandardSurveyItems', response.data.standardSurveyItems).then(() => {
                  this.countFetchedStandardSurveyItems = response.data.standardSurveyItems.length
                })
              })
            } else {
              console.log('%c fetchQMStandardSurveyItems standardSurveyItems empty ', 'background: red; color: white')
            }
          } else {
            console.log('%c fetchQMStandardSurveyItems standardSurveyItems null ', 'background: red; color: white')
          }
          this.resolveFetching(fetchname)
        })
      },
      fetchQMModes: function () {
        const fetchname = 'fetchQMModes'
        this.registerFetching(fetchname)
        axios.request({
          url: this.$getApiHost() + '/resource/modeway',
          method: 'get'
        }).then((response) => {
          if (response.data.modeways !== null) {
            if (response.data.modeways.length > 0) {
              this.$store.dispatch('clearModesStorage').then(() => {
                this.$store.dispatch('importModes', response.data.modeways).then(() => {
                  this.countFetchedModes = response.data.modeways.length
                })
              })
            } else {
              console.log('%c fetchQMModes modeways empty ', 'background: red; color: white')
            }
          } else {
            console.log('%c fetchQMModes modeways null ', 'background: red; color: white')
          }
          this.resolveFetching(fetchname)
        })
      },
      fetchQMCountries: function () {
        const fetchname = 'fetchQMCountries'
        this.registerFetching(fetchname)
        axios.request({
          url: this.$getApiHost() + '/resource/country',
          method: 'get'
        }).then((response) => {
          if (response.data.countries !== null) {
            if (response.data.countries.length > 0) {
              this.$store.dispatch('clearCountriesStorage').then(() => {
                this.$store.dispatch('importCountries', response.data.countries).then(() => {
                  this.countFetchedCountries = response.data.countries.length
                })
              })
            } else {
              console.log('%c fetchQMCountries countries empty ', 'background: red; color: white')
            }
          } else {
            console.log('%c fetchQMCountries countries null ', 'background: red; color: white')
          }
          this.resolveFetching(fetchname)
        })
      },
      fetchQMCrates: function () {
        const fetchname = 'fetchQMCrates'
        this.registerFetching(fetchname)
        axios.request({
          url: this.$getApiHost() + '/resource/crate',
          method: 'get'
        }).then((response) => {
          if (response.data.crates !== null) {
            if (response.data.crates.length > 0) {
              this.$store.dispatch('clearCratesStorage').then(() => {
                this.$store.dispatch('importCrates', response.data.crates).then(() => {
                  this.countFetchedCrates = response.data.crates.length
                })
              })
            } else {
              console.log('%c fetchQMCrates crates empty ', 'background: red; color: white')
            }
          } else {
            console.log('%c fetchQMCrates crates null ', 'background: red; color: white')
          }
          this.resolveFetching(fetchname)
        })
      },
      fetchQMVehicleTypes: function () {
        const fetchname = 'fetchQMVehicleTypes'
        this.registerFetching(fetchname)
        axios.request({
          url: this.$getApiHost() + '/resource/vehicletype',
          method: 'get'
        }).then((response) => {
          if (response.data.vehicleTypes !== null) {
            if (response.data.vehicleTypes.length > 0) {
              this.$store.dispatch('clearVehicleTypesStorage').then(() => {
                this.$store.dispatch('importVehicleTypes', response.data.vehicleTypes).then(() => {
                  this.countFetchedVehicleTypes = response.data.vehicleTypes.length
                })
              })
            } else {
              console.log('%c fetchQMVehicleTypes response empty ', 'background: red; color: white')
            }
          } else {
            console.log('%c fetchQMVehicleTypes response null ', 'background: red; color: white')
          }
          this.resolveFetching(fetchname)
        })
      },
      fetchQMLegalNotes: function () {
        const fetchname = 'fetchQMLegalNotes'
        this.registerFetching(fetchname)
        axios.request({
          url: this.$getApiHost() + '/resource/legalNote',
          method: 'get',
          headers: {
            'Authorization': 'Bearer ' + this.session.token
          }
        }).then((response) => {
          if (response.data.legalNote !== null) {
            if (response.data.legalNote.length > 0) {
              this.$store.dispatch('clearLegalNotesStorage').then(() => {
                this.$store.dispatch('importLegalNotes', response.data.legalNote).then(() => {
                  this.countFetchedLegalNotes = response.data.legalNote.length
                })
              })
            } else {
              console.log('%c fetchQMLegalNotes response empty ', 'background: red; color: white')
            }
          } else {
            console.log('%c fetchQMLegalNotes response null ', 'background: red; color: white')
          }
          this.resolveFetching(fetchname)
        })
      },
      closeFetchResults () {
        this.fetched = false
      },
      closeExportResults () {
        this.exportStarted = false
      },
      deleteConfirm () {
        this.deleteConfirmed = true
      },
      deleteTransmittedSurveys () {
        this.deleteConfirmError = null
        if (this.deleteConfirmPrompt.toLowerCase() === 'delete') {
          console.log('going to delete the transmitted surveys...')
          let deletedSurveys = this.$store.getters.getDeletedSurveys
          deletedSurveys.forEach(survey => {
            console.log('deleting fotos for survey ' + survey.surveyId + '...')
            this.$store.dispatch('deleteSurveyFotos', survey.surveyId).then(() => {
              console.log('deleting itmes for survey ' + survey.surveyId + '...')
              this.$store.dispatch('deleteSurveyItems', survey.surveyId).then(() => {
                console.log('deleting rooms for survey ' + survey.surveyId + '...')
                this.$store.dispatch('deleteSurveyRooms', survey.surveyId).then(() => {
                  console.log('deleting resources for survey ' + survey.surveyId + '...')
                  this.$store.dispatch('deleteSurveyResources', survey.surveyId).then(() => {
                    console.log('deleting signature for survey ' + survey.surveyId + '...')
                    this.$store.dispatch('deleteSignature', survey.surveyId).then(() => {
                      console.log('finally deleting the survey ' + survey.surveyId + ' iteself ...')
                      this.$store.dispatch('permanentlyDeleteSurveyFromStorage', survey.surveyId).then(() => {
                        console.log('refreshing surveys in app state from local storage')
                        this.refreshSurveys()
                      })
                    })
                  })
                })
              })
            })
          })
        } else {
          console.log('wrong confirmation message')
          this.deleteConfirmError = 'Wrong passphrase entered. Please type "delete" (without the quotes)' +
            ' to delete transferred surveys from your local app.'
        }
        this.deleteConfirmPrompt = ''
      },
      cancelDelete () {
        this.deleteConfirmed = false
      },
      // register a new api fetch (for tracking parallel api operations)
      registerFetching (fetchname) {
        // fetching and exporting simulatnously is not allowed
        this.exportStarted = false
        this.currentFetching.push(fetchname)
      },
      // deregister previously registered api fetch and dispatch actions if fetching completed
      resolveFetching (fetchname) {
        let index = this.currentFetching.indexOf(fetchname)
        if (index > -1) {
          this.currentFetching.splice(index, 1)
        }
        if (this.currentFetching.lenth === 0) {
          this.$store.dispatch('setLastFetch', moment().format())
        }
      },
      // register a new api export (for tracking parallel api operations)
      registerExporting (exportname) {
        // fetching and exporting simulatnously is not allowed
        this.fetched = false
        this.currentExporting.push(exportname)
      },
      // deregister previously registered api export
      resolveExporting (exportname) {
        let index = this.currentExporting.indexOf(exportname)
        if (index > -1) {
          this.currentExporting.splice(index, 1)
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/Sync.vue